/**
 * BuilderElementPreview
 * Pure content renderer — no card chrome, no meta headers.
 * Renders each element type as it would appear in a real proposal document.
 * Selection rings, toolbars, and resize handles are handled by CanvasPageStage.
 *
 * Data-bound elements (service_table, pricing_summary, company_info) pull from
 * real brand data and the service catalog so the canvas reflects actual content.
 */
import { useState, type CSSProperties, type ReactNode } from "react";
import {
  Building2,
  Check,
  ClipboardList,
  Image as ImageIcon,
  Pencil,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { BuilderElementDraft } from "./BuilderSectionElementList";
import { useServiceMenu } from "../../hooks/useServiceMenu";
import type { ServiceItem } from "../../types";

export interface ProposalLineItemPreview {
  id: string;
  name: string;
  description?: string | null;
  price?: number | null;
  quantity?: number | null;
  price_unit?: string | null;
  square_feet?: number | null;
}

export interface BrandPreviewData {
  companyName?: string | null;
  primaryLogoUrl?: string | null;
  secondaryLogoUrl?: string | null;
  phone?: string | null;
  website?: string | null;
  email?: string | null;
  address?: string | null;
  tagline?: string | null;
  /** Brand primary color hex — used for table headers, signature lines, totals */
  primaryColor?: string | null;
  /** Brand body text color — overrides default #111827 for all text elements */
  textColor?: string | null;
  /** Brand page background color — applied to page wrapper */
  backgroundColor?: string | null;
  /** Brand heading font CSS family string (for titles, h1/h2/h3) */
  headingFontFamily?: string | null;
  /** Brand body font CSS family string (for paragraphs, body copy) */
  bodyFontFamily?: string | null;
  /** Brand cover letter HTML — rendered in cover_letter pages */
  coverLetterHtml?: string | null;
  /** Brand signoff line — "Sincerely" / "Best regards" / etc. */
  signoff?: string | null;
  /** Proposal-specific context — threaded from ProposalBuilderEditor */
  prospectName?: string | null;
  prospectAddress?: string | null;
  lineItems?: ProposalLineItemPreview[];
  subtotal?: number | null;
  total?: number | null;
}

export interface LineItemUpdate {
  name?: string;
  price?: number | null;
  price_unit?: string | null;
  square_feet?: number | null;
}

export interface BuilderElementPreviewProps {
  element: BuilderElementDraft;
  className?: string;
  brand?: BrandPreviewData;
  pageSettings?: Record<string, unknown>;
  onUpdateLineItem?: (id: string, updates: LineItemUpdate) => void;
}

// ---------------------------------------------------------------------------
// Content accessors
// ---------------------------------------------------------------------------

function str(el: BuilderElementDraft, key: string, fallback = "") {
  const v = el.content?.[key];
  return typeof v === "string" ? v : fallback;
}
function num(el: BuilderElementDraft, key: string, fallback: number) {
  const v = el.content?.[key];
  return typeof v === "number" ? v : fallback;
}
function bool(el: BuilderElementDraft, key: string, fallback: boolean) {
  const v = el.content?.[key];
  return typeof v === "boolean" ? v : fallback;
}
function styleStr(el: BuilderElementDraft, key: string, fallback = "") {
  const v = el.style?.[key];
  return typeof v === "string" ? v : fallback;
}
function styleNum(el: BuilderElementDraft, key: string, fallback = 0) {
  const v = el.style?.[key];
  return typeof v === "number" ? v : fallback;
}

// ---------------------------------------------------------------------------
// Token resolver — replaces [Brand Tokens] with real brand data at render time
// ---------------------------------------------------------------------------

export function resolveText(text: string, brand?: BrandPreviewData): string {
  if (!brand) return text;
  let out = text;
  if (brand.companyName) {
    out = out.replace(/\[Company Name\]/gi, brand.companyName);
    out = out.replace(/\[Your Name\]/gi, brand.companyName);
    out = out.replace(/\[Business Name\]/gi, brand.companyName);
  }
  if (brand.prospectName) {
    out = out.replace(/\[Client Name\]/gi, brand.prospectName);
    out = out.replace(/\[Prospect Name\]/gi, brand.prospectName);
    out = out.replace(/\[Customer Name\]/gi, brand.prospectName);
  }
  if (brand.tagline) {
    out = out.replace(/\[Tagline\]/gi, brand.tagline);
  }
  if (brand.phone) {
    out = out.replace(/\[Phone\]/gi, brand.phone);
  }
  if (brand.email) {
    out = out.replace(/\[Email\]/gi, brand.email);
  }
  if (brand.website) {
    out = out.replace(/\[Website\]/gi, brand.website);
  }
  if (brand.signoff) {
    out = out.replace(/\[Signoff\]/gi, brand.signoff);
    out = out.replace(/\[Sign.?off\]/gi, brand.signoff);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Derived style from element.style
// ---------------------------------------------------------------------------

function buildTextStyle(el: BuilderElementDraft, brand?: BrandPreviewData): CSSProperties {
  const fw = styleStr(el, "fontWeight", "normal");
  const fontSize = styleNum(el, "fontSize", 16);
  const elementColor = styleStr(el, "color", "");
  const isHeading = fw === "bold" || fw === "semibold" || fontSize >= 24;
  return {
    fontSize,
    fontWeight: fw === "bold" ? 700 : fw === "semibold" ? 600 : fw === "medium" ? 500 : 400,
    color: elementColor || brand?.textColor || "#111827",
    textAlign: (styleStr(el, "textAlign", "left") as CSSProperties["textAlign"]) || "left",
    lineHeight: 1.7,
    fontFamily: isHeading && brand?.headingFontFamily ? brand.headingFontFamily : undefined,
  };
}

// ---------------------------------------------------------------------------
// Badge tone
// ---------------------------------------------------------------------------

function badgeTone(tone: string) {
  switch (tone) {
    case "success": return "bg-emerald-50 text-emerald-800 border-emerald-200";
    case "warning": return "bg-amber-50 text-amber-800 border-amber-200";
    case "info":    return "bg-sky-50 text-sky-800 border-sky-200";
    default:        return "bg-slate-100 text-slate-700 border-slate-200";
  }
}

// ---------------------------------------------------------------------------
// Alignment
// ---------------------------------------------------------------------------

function alignFlexClass(alignment?: string): string {
  switch (alignment) {
    case "center": return "justify-center";
    case "right":  return "justify-end";
    default:       return "justify-start";
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPrice(price: number | null | undefined): string {
  if (price == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(price);
}

function priceUnitLabel(unit: string | null | undefined): string {
  switch (unit) {
    case "per_clean":  return "/ clean";
    case "per_hour":   return "/ hr";
    case "per_sqft":   return "/ sqft";
    case "per_visit":  return "/ visit";
    case "fixed":      return "flat";
    default:           return "";
  }
}

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

function ImageRender({ el, brandLogoUrl }: { el: BuilderElementDraft; brandLogoUrl?: string | null }) {
  const src      = str(el, "src") || (el.type === "logo" ? brandLogoUrl ?? "" : "");
  const alt      = str(el, "alt") || (el.type === "logo" ? "Company Logo" : "");
  const caption  = str(el, "caption");
  const widthPx  = styleNum(el, "width", 0);
  const heightPx = styleNum(el, "imageHeight", 0);
  const radius   = styleNum(el, "borderRadius", 12);

  const imgStyle: CSSProperties = {
    width:        widthPx > 0 ? `${widthPx}px` : el.type === "logo" ? "auto" : "100%",
    height:       heightPx > 0 ? `${heightPx}px` : "auto",
    maxHeight:    el.type === "logo" && widthPx === 0 && heightPx === 0 ? "60px" : undefined,
    objectFit:    "contain",
    display:      "block",
    borderRadius: radius > 0 ? `${radius}px` : undefined,
  };

  return (
    <div>
      {src ? (
        <img src={src} alt={alt} style={imgStyle} />
      ) : (
        <div
          className="flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-black/14 bg-black/[0.025] text-center"
          style={{ borderRadius: `${radius}px`, width: widthPx > 0 ? `${widthPx}px` : "100%", minHeight: heightPx > 0 ? heightPx : el.type === "logo" ? 80 : 180 }}
        >
          <div className="rounded-xl border border-black/10 bg-white p-3 text-gray-400 shadow-sm">
            <ImageIcon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">
              {el.type === "logo" ? "No logo uploaded yet" : "No image yet"}
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {el.type === "logo" ? "Upload your logo in Brand Setup → Branding" : "Upload or paste a URL in the right panel"}
            </p>
          </div>
        </div>
      )}
      {caption && (
        <p className="mt-2 text-center text-sm leading-5 text-gray-500">{caption}</p>
      )}
    </div>
  );
}

const PRICE_UNIT_OPTIONS = [
  { value: "", label: "No unit" },
  { value: "per_clean", label: "/ clean" },
  { value: "per_hour", label: "/ hr" },
  { value: "per_sqft", label: "/ sqft" },
  { value: "per_visit", label: "/ visit" },
  { value: "fixed", label: "Flat" },
];

// ── Service table — React component so it can call useServiceMenu ─────────

function ServiceTableElement({
  el,
  accentColor,
  brand,
  pageSettings,
  onUpdateLineItem,
}: {
  el: BuilderElementDraft;
  accentColor: string;
  brand?: BrandPreviewData;
  pageSettings?: Record<string, unknown>;
  onUpdateLineItem?: (id: string, updates: LineItemUpdate) => void;
}) {
  const hasProposalItems = (brand?.lineItems?.length ?? 0) > 0;
  const { data: catalogServices = [] } = useServiceMenu(!hasProposalItems);
  const showHeading      = bool(el, "showHeading", true);
  const heading          = str(el, "headingText") || "Services & Pricing";
  const radius           = styleNum(el, "borderRadius", 12);
  const showDescriptions = pageSettings?.showDescriptions !== false;
  const showUnits        = pageSettings?.showUnits        !== false;
  const canEdit          = hasProposalItems && !!onUpdateLineItem;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; price: string; price_unit: string; square_feet: string }>({
    name: "", price: "", price_unit: "", square_feet: "",
  });

  const rows: Array<{
    id: string;
    name: string;
    description?: string | null;
    price_unit?: string | null;
    price?: number | null;
    square_feet?: number | null;
    lineTotal?: number | null;
  }> =
    hasProposalItems
      ? (brand!.lineItems!).map((li) => {
          const isPerSqFt = li.price_unit === "per_sqft" && li.square_feet != null;
          return {
            id: li.id,
            name: li.name,
            description: li.description,
            price_unit: li.price_unit,
            price: li.price,
            square_feet: li.square_feet ?? null,
            lineTotal: isPerSqFt
              ? (li.price ?? 0) * (li.square_feet ?? 0)
              : li.price != null && li.quantity != null
                ? li.price * li.quantity
                : li.price,
          };
        })
      : catalogServices
          .filter((s: ServiceItem) => s.active !== false)
          .map((s: ServiceItem) => {
            const isPerSqFt = s.price_unit === "per_sqft" && s.default_square_feet != null;
            return {
              id: s.id,
              name: s.name,
              description: s.default_description,
              price_unit: s.price_unit,
              price: s.default_price,
              square_feet: s.default_square_feet ?? null,
              lineTotal: isPerSqFt
                ? (s.default_price ?? 0) * (s.default_square_feet ?? 0)
                : s.default_price,
            };
          });

  function startEdit(row: typeof rows[number]) {
    setEditingId(row.id);
    setDraft({
      name: row.name,
      price: row.price != null ? String(row.price) : "",
      price_unit: row.price_unit ?? "",
      square_feet: row.square_feet != null ? String(row.square_feet) : "",
    });
  }

  function commitEdit(id: string) {
    if (!onUpdateLineItem) return;
    const priceNum   = parseFloat(draft.price);
    const sqFtNum    = parseFloat(draft.square_feet);
    const isPerSqFt  = draft.price_unit === "per_sqft";
    onUpdateLineItem(id, {
      name:        draft.name.trim() || undefined,
      price:       Number.isFinite(priceNum) ? priceNum : undefined,
      price_unit:  draft.price_unit || null,
      square_feet: isPerSqFt && Number.isFinite(sqFtNum) ? sqFtNum : null,
    });
    setEditingId(null);
  }

  function cancelEdit() {
    setEditingId(null);
  }

  const emptyLabel = hasProposalItems
    ? "No line items added yet"
    : "No services yet — add them in Brand Setup → Services";

  return (
    <div className="w-full space-y-3">
      {showHeading && (
        <p className="text-xl font-semibold tracking-tight text-gray-800">{heading}</p>
      )}
      <div className="overflow-hidden border border-black/10" style={{ borderRadius: radius }}>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr style={{ backgroundColor: accentColor }}>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-white">Service</th>
              {showUnits && <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-white">Details</th>}
              <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-white">Total</th>
              {canEdit && <th className="w-16 px-2 py-3" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={(showUnits ? 2 : 1) + 1 + (canEdit ? 1 : 0)} className="px-4 py-6 text-center text-sm text-gray-400">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((s) => {
                const isEditing = editingId === s.id;
                return (
                  <tr key={s.id} className="group border-b border-black/6 last:border-0">
                    {isEditing ? (
                      <>
                        <td className="px-3 py-2">
                          <input
                            autoFocus
                            value={draft.name}
                            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") commitEdit(s.id); if (e.key === "Escape") cancelEdit(); }}
                            className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-800 focus:border-gray-500 focus:outline-none"
                          />
                        </td>
                        {showUnits && (
                          <td className="px-3 py-2 text-right">
                            <div className="flex flex-col items-end gap-1">
                              <select
                                value={draft.price_unit}
                                onChange={(e) => setDraft((d) => ({ ...d, price_unit: e.target.value }))}
                                className="rounded border border-gray-300 bg-white px-1 py-1 text-xs text-gray-700 focus:outline-none"
                              >
                                {PRICE_UNIT_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              {draft.price_unit === "per_sqft" && (
                                <input
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={draft.square_feet}
                                  onChange={(e) => setDraft((d) => ({ ...d, square_feet: e.target.value }))}
                                  placeholder="sq ft"
                                  className="w-20 rounded border border-gray-300 bg-white px-2 py-1 text-right text-xs text-gray-800 focus:border-gray-500 focus:outline-none"
                                />
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            step={0.01}
                            value={draft.price}
                            onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") commitEdit(s.id); if (e.key === "Escape") cancelEdit(); }}
                            placeholder={draft.price_unit === "per_sqft" ? "rate/sqft" : "price"}
                            className="w-24 rounded border border-gray-300 bg-white px-2 py-1 text-right text-sm text-gray-800 focus:border-gray-500 focus:outline-none"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center justify-end gap-1">
                            <button type="button" onClick={() => commitEdit(s.id)} className="rounded p-1 text-emerald-600 hover:bg-emerald-50">
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" onClick={cancelEdit} className="rounded p-1 text-gray-400 hover:bg-gray-100">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{s.name}</p>
                          {showDescriptions && s.description && (
                            <p className="mt-0.5 text-xs text-gray-400 line-clamp-1">{s.description}</p>
                          )}
                        </td>
                        {showUnits && (
                          <td className="px-4 py-3 text-right text-xs text-gray-500">
                            {s.price_unit === "per_sqft" && s.square_feet != null ? (
                              <span>
                                {s.square_feet.toLocaleString()} sq ft
                                <br />
                                <span className="text-gray-400">${s.price?.toFixed(2) ?? "—"}/sqft</span>
                              </span>
                            ) : (
                              priceUnitLabel(s.price_unit)
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 text-right font-medium text-gray-700">
                          {formatPrice(s.lineTotal ?? s.price)}
                        </td>
                        {canEdit && (
                          <td className="px-2 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => startEdit(s)}
                              className="rounded p-1 text-gray-300 opacity-0 transition group-hover:opacity-100 hover:bg-gray-100 hover:text-gray-600"
                              title="Edit this service"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {canEdit && editingId === null && (
        <p className="text-[11px] text-gray-400">Click the pencil icon on any row to edit that service for this proposal.</p>
      )}
    </div>
  );
}

// ── Pricing summary — React component so it can call useServiceMenu ────────

function PricingElement({
  el,
  accentColor,
  brand,
  pageSettings,
}: {
  el: BuilderElementDraft;
  accentColor: string;
  brand?: BrandPreviewData;
  pageSettings?: Record<string, unknown>;
}) {
  const hasProposalItems = (brand?.lineItems?.length ?? 0) > 0;
  const { data: catalogServices = [] } = useServiceMenu(!hasProposalItems);
  const showHeading = bool(el, "showHeading", true);
  const heading     = str(el, "headingText") || "Pricing Summary";

  interface PricingRow { id: string; name: string; lineTotal: number | null }

  const rows: PricingRow[] = hasProposalItems
    ? brand!.lineItems!.map((li) => ({
        id: li.id,
        name: li.name,
        lineTotal: li.price != null && li.quantity != null
          ? li.price * li.quantity
          : li.price ?? null,
      }))
    : catalogServices
        .filter((s: ServiceItem) => s.active !== false && s.default_price != null)
        .map((s: ServiceItem) => ({ id: s.id, name: s.name, lineTotal: s.default_price ?? null }));

  const subtotal    = hasProposalItems
    ? (brand!.subtotal ?? rows.reduce((sum, r) => sum + (r.lineTotal ?? 0), 0))
    : rows.reduce((sum, r) => sum + (r.lineTotal ?? 0), 0);
  const hasItems    = rows.length > 0;
  const totalLabel  = hasProposalItems ? "Total" : "Est. Total";
  const showTotals  = pageSettings?.showTotals !== false;

  return (
    <div className="w-full space-y-3">
      {showHeading && (
        <p className="text-xl font-semibold tracking-tight text-gray-800">{heading}</p>
      )}
      <div className="rounded-2xl border border-black/10 bg-black/[0.02] p-5 space-y-2">
        {hasItems ? (
          <>
            {rows.slice(0, 4).map((r) => (
              <div key={r.id} className="flex justify-between text-sm">
                <span className="text-gray-600 truncate pr-4">{r.name}</span>
                <span className="shrink-0" style={{ color: "#4b5563" }}>{formatPrice(r.lineTotal)}</span>
              </div>
            ))}
            {rows.length > 4 && (
              <p className="text-xs text-gray-400">+{rows.length - 4} more items</p>
            )}
            {showTotals && (
              <div className="flex justify-between border-t border-black/10 pt-3 mt-3">
                <span className="text-base font-semibold text-gray-800">{totalLabel}</span>
                <span className="text-base font-bold" style={{ color: accentColor }}>
                  {formatPrice(subtotal)}
                </span>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-center text-gray-400 py-3">
            {hasProposalItems
              ? "No line items added yet"
              : "No priced services yet — add them in Brand Setup → Services"}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Signature block ────────────────────────────────────────────────────────

function SignatureElement({
  el,
  accentColor,
  pageSettings,
}: {
  el: BuilderElementDraft;
  accentColor: string;
  pageSettings?: Record<string, unknown>;
}) {
  const showHeading = bool(el, "showHeading", true);
  const heading     = str(el, "headingText") || "Authorization & Signature";

  const requireSignature     = pageSettings?.requireSignature     !== false;
  const requirePrintedName   = pageSettings?.requirePrintedName   !== false;
  const requireSignedDate    = pageSettings?.requireSignedDate    !== false;
  const showTerms            = pageSettings?.showTermsAcknowledgement !== false;
  const approvalText         = typeof pageSettings?.approvalText === "string" ? pageSettings.approvalText : "";
  const signerLabel          = typeof pageSettings?.signerLabel   === "string" ? pageSettings.signerLabel : "Authorized Representative";

  const fields = [
    requirePrintedName  && { label: "Printed Name",      sub: signerLabel },
    requireSignature    && { label: "Client Signature",   sub: signerLabel },
    requireSignedDate   && { label: "Date",               sub: "MM / DD / YYYY" },
  ].filter((f): f is { label: string; sub: string } => Boolean(f));

  return (
    <div className="space-y-5">
      {showHeading && (
        <p className="text-xl font-semibold tracking-tight text-gray-800">{heading}</p>
      )}
      {showTerms && approvalText && (
        <p className="text-sm text-gray-500 leading-relaxed">{approvalText}</p>
      )}
      <div className="grid gap-8 md:grid-cols-2">
        {fields.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No signer fields selected.</p>
        ) : fields.map(({ label, sub }) => (
          <div key={label}>
            <div className="h-14 border-b-2" style={{ borderColor: accentColor }} />
            <p className="mt-2 text-sm font-medium" style={{ color: "#374151" }}>{label}</p>
            <p className="mt-0.5 text-xs" style={{ color: "#9ca3af" }}>{sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DataRender({
  el,
  defaultTitle,
  body,
  icon,
}: {
  el:           BuilderElementDraft;
  defaultTitle: string;
  body:         string;
  icon:         ReactNode;
}) {
  const showHeading = bool(el, "showHeading", true);
  const heading     = str(el, "headingText") || defaultTitle;

  return (
    <div className="space-y-3">
      {showHeading && (
        <p className="text-xl font-semibold tracking-tight text-gray-800">{heading}</p>
      )}
      <div className="flex items-start gap-3 rounded-2xl border border-black/10 bg-black/[0.02] px-5 py-4">
        <div className="mt-0.5 shrink-0 text-gray-400">{icon}</div>
        <p className="text-sm leading-6 text-gray-500">{body}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function BuilderElementPreview({ element, className, brand, pageSettings, onUpdateLineItem }: BuilderElementPreviewProps) {
  const textStyle   = buildTextStyle(element, brand);
  const bg          = styleStr(element, "backgroundColor", "");
  const radius      = styleNum(element, "borderRadius", 0);
  const divLabel    = str(element, "label");
  const alignment   = styleStr(element, "alignment", "left");
  // Per-element accent color overrides brand primary (set in inspector)
  const elementAccentColor = str(element, "accentColor");
  // Accent color is ONLY from element-level override — never inherits brand primary color.
  // User sets it explicitly via the inspector color picker.
  const accentColor = elementAccentColor || "#374151";

  const containerStyle: CSSProperties = {
    backgroundColor: bg || undefined,
    borderRadius:    radius > 0 ? `${radius}px` : undefined,
  };

  return (
    <div
      className={cn("min-h-[1px] flex w-full", alignFlexClass(alignment), className)}
      style={containerStyle}
    >

      {/* ── Text ─────────────────────────────────────────────── */}
      {element.type === "text" && (
        <p style={textStyle} className="w-full whitespace-pre-wrap">
          {resolveText(str(element, "text", "Add your text here."), brand)}
        </p>
      )}

      {/* ── Rich text ────────────────────────────────────────── */}
      {element.type === "rich_text" && (() => {
        const isBrandCoverLetter = str(element, "source") === "brand_cover_letter";
        const elementHtml = str(element, "html", "<p>Add rich text here.</p>");
        // Also treat the old default placeholder as a cover-letter slot so existing
        // templates automatically show real brand content without migration.
        const isOldPlaceholder =
          elementHtml.includes("Dear [Client Name]") &&
          elementHtml.includes("Thank you for the opportunity");
        const useBrandContent =
          (isBrandCoverLetter || isOldPlaceholder) && !!brand?.coverLetterHtml;
        const isCoverLetterSlot = isBrandCoverLetter || isOldPlaceholder;

        const rawHtml = useBrandContent
          ? brand!.coverLetterHtml!
          : elementHtml;
        const resolvedHtml = useBrandContent
          ? rawHtml // brand HTML is already real prose — no token replacement needed
          : resolveText(rawHtml, brand);
        return (
          <div className="w-full">
            {bool(element, "showHeading", false) && str(element, "headingText") && (
              <p className="mb-3 text-xl font-semibold tracking-tight text-gray-800">
                {resolveText(str(element, "headingText"), brand)}
              </p>
            )}
            {isCoverLetterSlot && !brand?.coverLetterHtml && (
              <p className="mb-3 text-xs text-gray-400 italic">
                Set up your cover letter in Brand Setup → Cover Letter to see it here.
              </p>
            )}
            <div
              className="prose prose-sm max-w-none"
              style={brand?.textColor ? {
                "--tw-prose-body": brand.textColor,
                "--tw-prose-headings": brand.textColor,
                "--tw-prose-bold": brand.textColor,
                color: brand.textColor,
              } as CSSProperties : undefined}
              dangerouslySetInnerHTML={{ __html: resolvedHtml }}
            />
          </div>
        );
      })()}

      {/* ── Image / Logo ─────────────────────────────────────── */}
      {(element.type === "image" || element.type === "logo") && (
        <ImageRender el={element} brandLogoUrl={brand?.primaryLogoUrl} />
      )}

      {/* ── Divider ──────────────────────────────────────────── */}
      {element.type === "divider" && (
        <div className="w-full py-2">
          {divLabel && (
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
              {divLabel}
            </p>
          )}
          <div
            className={cn(
              "w-full border-t",
              str(element, "style", "solid") === "dashed"
                ? "border-dashed border-black/25"
                : str(element, "style", "solid") === "subtle"
                ? "border-black/12"
                : "border-black/28",
            )}
          />
        </div>
      )}

      {/* ── Spacer ───────────────────────────────────────────── */}
      {element.type === "spacer" && (
        <div className="w-full" style={{ height: num(element, "height", 24) }} />
      )}

      {/* ── Badge ────────────────────────────────────────────── */}
      {element.type === "badge" && (
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-4 py-1.5 text-xs font-semibold tracking-wide",
            badgeTone(str(element, "tone", "default")),
          )}
        >
          {str(element, "text", "Badge")}
        </span>
      )}

      {/* ── Service table — uses proposal line items or brand catalog ── */}
      {element.type === "service_table" && (
        <ServiceTableElement el={element} accentColor={accentColor} brand={brand} pageSettings={pageSettings} onUpdateLineItem={onUpdateLineItem} />
      )}

      {/* ── Pricing summary — uses proposal line items or brand catalog ── */}
      {element.type === "pricing_summary" && (
        <PricingElement el={element} accentColor={accentColor} brand={brand} pageSettings={pageSettings} />
      )}

      {/* ── Signature block ───────────────────────────────────── */}
      {element.type === "signature_block" && (
        <SignatureElement el={element} accentColor={accentColor} pageSettings={pageSettings} />
      )}

      {/* ── Company info ──────────────────────────────────────── */}
      {element.type === "company_info" && (
        <div className="w-full">
          {brand?.companyName ? (
            <div className="space-y-1 text-sm text-gray-700">
              <p className="font-semibold text-gray-900">{brand.companyName}</p>
              {brand.tagline && <p className="text-xs text-gray-500">{brand.tagline}</p>}
              {brand.phone && <p>{brand.phone}</p>}
              {brand.email && <p>{brand.email}</p>}
              {brand.website && <p>{brand.website}</p>}
              {brand.address && <p className="text-xs text-gray-500">{brand.address}</p>}
            </div>
          ) : (
            <DataRender
              el={element}
              defaultTitle="Company Info"
              body="Your company name, phone, email, and website render here from Brand Setup."
              icon={<Building2 className="h-5 w-5" />}
            />
          )}
        </div>
      )}

      {/* ── Project summary ───────────────────────────────────── */}
      {element.type === "project_summary" && (
        <div className="w-full">
          {brand?.prospectName ? (
            <div className="space-y-1 text-sm">
              <p className="font-semibold text-gray-900">{brand.prospectName}</p>
              {brand.prospectAddress && (
                <p className="text-xs text-gray-500">{brand.prospectAddress}</p>
              )}
            </div>
          ) : (
            <DataRender
              el={element}
              defaultTitle="Prospect Details"
              body="Prospect name and address render here from the proposal."
              icon={<ClipboardList className="h-5 w-5" />}
            />
          )}
        </div>
      )}

      {/* ── Static / Custom ───────────────────────────────────── */}
      {(element.type === "static_content" || element.type === "custom") && (
        <div className="w-full">
          {str(element, "headingText") && (
            <p className="mb-3 text-xl font-semibold tracking-tight text-gray-800">
              {resolveText(str(element, "headingText"), brand)}
            </p>
          )}
          <p style={textStyle} className="whitespace-pre-wrap">
            {resolveText(str(element, "text", "Add custom content here."), brand)}
          </p>
        </div>
      )}

    </div>
  );
}
